import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ListCard } from '@/components/atoms/list-card';
import { DataStatusIndicator } from '@/components/molecules/data-status-indicator/data-status-indicator';
import { CompactPagination } from '@/components/organisms/compact-pagination';
import { useDestinations } from '@/features/destinations/hooks/query/use-destinations';
import { Query } from '../../../../../components/atoms/query';
import { CreateVisitFormValues } from '../../organisms/create-visit/schema/create-visit-validation-schema';

import {
  CreateVisitContext,
  CreateVisitEvent,
} from '@/features/visits/utils/machine/create-visit-machine';
import { useKey } from '@/hooks/use-key';
import { usePagination } from '@/hooks/use-pagination';

interface Props {
  context: CreateVisitContext;
  send: (event: CreateVisitEvent) => void;
}

export const DestinationInfo = ({ context, send }: Props) => {
  const { t } = useTranslation(['common', 'createVisit']);

  const { control, watch } = useFormContext<CreateVisitFormValues>();

  const [query, setQuery] = useState('');

  const host = watch('host');
  const hostDestinations = host?.destinations ?? [];

  const {
    changePage,
    changePerPage,
    page,
    perPage,
    setTotalItems,
    totalPages,
    nextDisabled,
    nextPage,
    prevDisabled,
    prevPage,
  } = usePagination();

  const {
    data: allDestinationsData,
    isLoading: isLoadingAllDestinations,
    isFetching: isFetchingAllDestinations,
    error: allDestinationsError,
  } = useDestinations({
    page,
    perPage,
    search: query.trim(),
    enabled: !host,
  });

  useEffect(() => {
    if (allDestinationsData && !host) {
      setTotalItems(allDestinationsData.total);
    }
  }, [allDestinationsData, host, setTotalItems]);

  const sourceDestinations = host ? hostDestinations : (allDestinationsData?.resources ?? []);

  const displayedDestinations = !host
    ? sourceDestinations
    : sourceDestinations.filter(dest =>
        dest.name.toLowerCase().includes(query.trim().toLowerCase()),
      );

  const destinations = watch('destinations');
  const nextStepDisabled = destinations.length === 0;

  const nextStep = () => {
    if (nextStepDisabled) {
      return;
    }

    send({ type: 'NEXT' });
  };

  useKey('Enter', nextStep, !nextStepDisabled);

  return (
    <div>
      <Box
        sx={{
          mb: 2,
          py: 2,
        }}>
        <Typography color="textPrimary" sx={{ mb: 0.5 }} variant="h6">
          {t('common:step')} {context.step}. {t(context.content)}
        </Typography>
      </Box>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Query
            searchAsType={true}
            onChange={newQuery => {
              setQuery(newQuery);
              changePage(1);
            }}
            value={query}
            disabled={!!host && hostDestinations.length < 1}
          />
        </Box>
        <Controller
          control={control}
          name="destinations"
          render={({ field: { value, onChange } }) => {
            return (
              <>
                {!!host && hostDestinations.length < 1 ? (
                  <Typography>{t('createVisit:destination.noAssignedDest')}</Typography>
                ) : (
                  <>
                    {!host && (
                      <DataStatusIndicator
                        dataLength={allDestinationsData?.resources?.length ?? 0}
                        isFetching={isLoadingAllDestinations}
                        error={allDestinationsError?.response?.data?.message}
                      />
                    )}
                    {displayedDestinations.map(({ id, name, description }) => {
                      const isSelected = value?.some(dest => dest.id === id);

                      return (
                        <ListCard
                          key={id}
                          name={name}
                          description={description}
                          isSelected={isSelected}
                          onClick={() => {
                            if (isSelected) {
                              onChange(value.filter(dest => dest.id !== id));
                              return;
                            }

                            onChange([...value, { id, name, description }]);
                          }}
                        />
                      );
                    })}
                  </>
                )}
              </>
            );
          }}
        />
        {!host && (
          <CompactPagination
            page={page}
            perPage={perPage}
            totalPages={totalPages}
            changePage={changePage}
            disabled={isLoadingAllDestinations}
            handlePerPageChange={changePerPage}
            nextDisabled={nextDisabled}
            isFetching={isFetchingAllDestinations}
            nextPage={nextPage}
            prevDisabled={prevDisabled}
            prevPage={prevPage}
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 'auto',
          pt: 3,
        }}>
        <Button
          color="primary"
          onClick={() => send({ type: 'BACK' })}
          size="large"
          sx={{ mr: 2 }}
          type="button"
          variant="text">
          {t('common:button.back')}
        </Button>
        <Button
          color="primary"
          size="large"
          type="button"
          disabled={nextStepDisabled}
          onClick={nextStep}
          variant="contained">
          {t('common:button.nextStep')}
        </Button>
      </Box>
    </div>
  );
};
